/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Copy,
  DownloadSimple,
  Eye,
  EyeSlash,
  Plus,
  X,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useReactFlow, useStore } from 'reactflow';
import { useAuthStore } from '../../../store/storeAuth';
import { useNotificationStore } from '../../../store/storeNotifications';
import IconButtonTooltip from '../../UI/IconButtonTooltip/IconButtonTooltip';
import KitModal from '../../UI/KitModal';

import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import { FluxService } from '../../../service/FluxService';
import useFlowsStore from '../../../store/storeFlows';

import 'react18-json-view/src/dark.css';
import { useSessionStore } from '../../../store/storeSessions';
import { buildApiSchemaMarkdown } from '../../../util/apiSchema/buildApiSchemaMarkdown';
import { buildApiSchemaTableRowsWithPurposes } from '../../../util/apiSchema/dataInputPurposeForApiSchema';
import {
  SUMMARY_FLOW_VAR_INPUT_ID,
  extractTextFromFluxProductionMessage,
  getFluxProductionErrorMessage,
} from '../../../util/apiSchema/fluxProductionMessageText';
import { sanitizeFileName } from '../../../util/apiSchema/sanitizeFileName';
import {
  buildVariableInputsConfigJson,
  collectVarInputSnapshotsFromNodeInternals,
  toConfigDisplayString,
} from '../../../util/apiSchema/variableInputsExample';
import SmallMemorySessionModal from '../../SmallMemory/SmallMemorySessionModal';

type Props = {
  onHide: () => void;
  show: boolean;
  formData: any;
  onChange: (e: any) => void;
  flowId?: string;
  isPublic?: boolean;
  isWebhook?: boolean;
  onChangeIsPublicOrWebhook?: (e: any) => void;
  spaceId?: string;
  onChangeSessionId?: (e: any) => void;
  onCreateNewSession: (name: string) => void;
};

const SettingsModal = ({
  onHide,
  show,
  formData,
  onChange,
  flowId,
  isPublic,
  isWebhook,
  onChangeIsPublicOrWebhook,
  onCreateNewSession,
}: Props) => {
  const { getNodes, getEdges } = useReactFlow();
  const { flows } = useFlowsStore(state => state);

  const nodes = useStore(store => store.nodeInternals);

  const nodesDependency = useMemo(() => {
    const nodesArray = Array.from(nodes.values());
    const varInputNodes = nodesArray.filter(
      node => node?.type === 'varInputNode',
    );
    return JSON.stringify(
      varInputNodes.map(node => ({
        id: node?.id,
        text: node?.data?.text,
        label: node?.data?.label,
      })),
    );
  }, [nodes]);
  const { user } = useAuthStore(state => state);
  const [flowSummary, setFlowSummary] = useState<string>(
    formData.summary || '',
  );
  const [isLoadingSummary, setIsLoadingSummary] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryJsonExpanded, setSummaryJsonExpanded] =
    useState<boolean>(false);
  const [originalSummary, setOriginalSummary] = useState<string>(
    formData.summary || '',
  );
  const [userEdited, setUserEdited] = useState<boolean>(false);
  const [showJson, setShowJson] = useState<boolean>(false);
  const [isJsonEditable, setIsJsonEditable] = useState<boolean>(false);
  const [editedFlowJson, setEditedFlowJson] = useState<string>('');
  const [originalFlowJson, setOriginalFlowJson] = useState<string>('');
  const [isLoadingChangeAgent, setIsLoadingChangeAgent] =
    useState<boolean>(false);
  const [showFilesExample, setShowFilesExample] = useState(false);
  const [showAgentSessionModal, setShowAgentSessionModal] =
    useState<boolean>(false);
  const [sessionName, setSessionName] = useState<string>('');
  const [hideDocumentation, setHideDocumentation] = useState<boolean>(false);

  const setNotification = useNotificationStore(state => state.setNotification);

  const { activeSession, setActiveSession, sessions } = useSessionStore(
    state => state,
  );

  const activeFlow = flows.find(flow => flow.id === flowId);

  useEffect(() => {
    if (formData.summary && formData.summary !== flowSummary) {
      setFlowSummary(formData.summary);
      setOriginalSummary(formData.summary);
      setUserEdited(false);
    }
  }, [formData.summary, flowSummary]);

  const flowJson = useMemo(() => {
    const nodes = getNodes();
    const edges = getEdges();
    const flowData = { nodes, edges };
    return JSON.stringify(flowData, null, 2);
  }, [getNodes, getEdges]);

  // Initialize originalFlowJson when flowJson changes and not in edit mode
  useEffect(() => {
    if (!isJsonEditable && flowJson) {
      setOriginalFlowJson(flowJson);
      setEditedFlowJson(flowJson);
    }
  }, [flowJson, isJsonEditable]);

  const config = useMemo(() => {
    const snapshots = collectVarInputSnapshotsFromNodeInternals(nodes);
    return buildVariableInputsConfigJson(snapshots);
  }, [nodesDependency, nodes]);

  const configFull = useMemo(() => {
    const nodesArray = Array.from(nodes.values());
    const varInputNodes = nodesArray?.filter(
      node => node?.type === 'varInputNode',
    );

    if (!varInputNodes.length) {
      return 'No var input nodes found';
    }

    const rawJson = JSON.stringify(
      {
        variableInputs: varInputNodes.map((node, idx) => ({
          inputId: node?.id,
          inputText: node?.data.text || '',
          links: [
            {
              name: 'Descriptive name of the link (optional)',
              url: 'File URL (optional)',
            },
          ],
          files: [
            {
              name: 'File name with extension (optional)',
              base64: 'File content encoded in base64 (optional)',
            },
          ],
          isExtraction:
            'Boolean: indicates if this input is for data extraction (optional, options: true, false)',
          typeOCR:
            "String: type of OCR applied to the input (optional, default: 'mistral_ocr', options: 'mistral_ocr', 'vision_ocr', 'google_ocr')",
        })),
      },
      null,
      2,
    );

    return rawJson;
  }, [nodesDependency, nodes]);

  const configDisplay = useMemo(
    () => toConfigDisplayString(config, hideDocumentation),
    [config, hideDocumentation],
  );

  const nodeOptionsApiInput = useMemo(() => {
    const nodesArray = Array.from(nodes.values());
    const varInputNodes = nodesArray?.filter(
      node => node?.type === 'varInputNode',
    );

    if (!varInputNodes.length) {
      return [];
    }

    return varInputNodes.map(node => ({
      value: node?.id,
      label: node?.data?.label,
    }));
  }, [nodesDependency, nodes]);

  const apiKey = user?.apiKey;

  const copyFlow = () => {
    navigator.clipboard.writeText(activeFlow?.id || '');
    setNotification({
      message: 'Agent ID copied to clipboard',
      type: 'success',
    });
  };

  const copyFlowEmail = () => {
    navigator.clipboard.writeText(
      `${activeFlow?.id}@${import.meta.env.VITE_FLOW_EMAIL}`,
    );
    setNotification({
      message: 'Agent Email copied to clipboard',
      type: 'success',
    });
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(
      `${import.meta.env.VITE_API_URL}/flux/api-v2-webhook?flowId=${
        activeFlow?.id
      }`,
    );
    setNotification({
      message: 'Agent Webhook URL copied to clipboard',
      type: 'success',
    });
  };

  const copyFlowJson = () => {
    navigator.clipboard.writeText(flowJson);
    setNotification({
      message: 'Agent JSON copied to clipboard',
      type: 'success',
    });
  };

  const generateFlowSummary = useCallback(async () => {
    if (flowSummary && !isLoadingSummary) {
      const confirmRegenerate = window.confirm(
        'Are you sure you want to regenerate the summary? This will overwrite your current summary.',
      );
      if (!confirmRegenerate) {
        return;
      }
    }

    setIsLoadingSummary(true);
    setSummaryError(null);

    try {
      const nodes = getNodes();
      const edges = getEdges();
      const flowData = { nodes, edges };
      const flowDataString = JSON.stringify(flowData, null, 2);

      const truncateJsonValues = (obj: any, maxLength = 300): any => {
        if (!obj) return obj;

        if (typeof obj === 'object') {
          if (Array.isArray(obj)) {
            return obj.map(item => truncateJsonValues(item, maxLength));
          } else {
            const result: Record<string, any> = {};
            for (const key in obj) {
              result[key] = truncateJsonValues(obj[key], maxLength);
            }
            return result;
          }
        } else if (typeof obj === 'string' && obj.length > maxLength) {
          return obj.substring(0, maxLength) + '...';
        }

        return obj;
      };

      const parsedFlowData = JSON.parse(flowDataString);
      const truncatedFlowData = truncateJsonValues(parsedFlowData);
      const truncatedFlowDataString = JSON.stringify(
        truncatedFlowData,
        null,
        2,
      );

      const summaryFlowId = import.meta.env.VITE_SUMMARY_FLOW_ID || '';

      const variableInputs = [
        {
          inputId: SUMMARY_FLOW_VAR_INPUT_ID,
          inputText: truncatedFlowDataString,
          links: [],
          files: [],
        },
      ];

      const response = await FluxService.FluxApiProduction(summaryFlowId, {
        variableInputs,
      });

      const responseData = response?.data;
      const apiErr = getFluxProductionErrorMessage(responseData);
      if (apiErr) {
        console.error('API returned an error:', apiErr);
        setSummaryError(`API Error: ${apiErr}`);
        setNotification({
          type: 'error',
          message: `Error generating agent summary: ${apiErr}`,
          duration: 5000,
        });
        return;
      }

      const summaryText = extractTextFromFluxProductionMessage(
        responseData,
        'No summary available',
      );

      setFlowSummary(summaryText);
      setOriginalSummary(summaryText);
      setUserEdited(false);

      if (summaryText !== 'No summary available') {
        onChange({
          target: {
            name: 'summary',
            value: summaryText,
          },
        });
      }

      setNotification({
        type: 'success',
        message: 'Agent summary generated successfully',
        duration: 3000,
      });
    } catch (error: any) {
      console.error('Error generating agent summary:', error);

      let errorMessage = error instanceof Error ? error.message : String(error);
      try {
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message || errorMessage;
        }
      } catch (e) {}

      setSummaryError(`Error: ${errorMessage}`);
      setNotification({
        type: 'error',
        message: `Error generating agent summary: ${errorMessage}`,
        duration: 5000,
      });
    } finally {
      setIsLoadingSummary(false);
    }
  }, [getNodes, getEdges, setNotification, onChange]);

  useEffect(() => {
    const shouldGenerateSummary =
      show && !flowSummary && !formData.summary && !isLoadingSummary;

    if (shouldGenerateSummary) {
      generateFlowSummary();
    }
  }, [
    show,
    flowSummary,
    isLoadingSummary,
    generateFlowSummary,
    formData.summary,
  ]);

  const generateLinkForFlowId = useMemo(() => {
    if (window.location.origin.includes('localhost')) {
      return `http://localhost:3001/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('staging')) {
      return `https://api-staging.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('app')) {
      return `https://api.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('dev')) {
      return `https://api-dev.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }
  }, [flowId]);

  const buildApiSchemaMarkdownDocument =
    useCallback(async (): Promise<string> => {
      if (!flowId || !generateLinkForFlowId) {
        return '';
      }
      const snapshots = collectVarInputSnapshotsFromNodeInternals(nodes);
      const configRaw = buildVariableInputsConfigJson(snapshots);
      const fullExample = toConfigDisplayString(configRaw, true);
      const apiBaseUrl = generateLinkForFlowId.split('?')[0] ?? '';
      const exampleUrlWithSession = formData.generateSessionId
        ? `${generateLinkForFlowId}&sessionId=[my-session-abc]`
        : generateLinkForFlowId;
      const summaryText = flowSummary || formData.summary || '';
      const summaryFlowId = import.meta.env.VITE_SUMMARY_FLOW_ID || '';
      const tableRows = await buildApiSchemaTableRowsWithPurposes({
        snapshots,
        summaryFlowId,
        agentSummary: summaryText,
      });
      return buildApiSchemaMarkdown({
        agentName: activeFlow?.name ?? 'Agent',
        flowId,
        summary: summaryText,
        sessionMode: formData.generateSessionId ? 'required' : 'not_required',
        inputCount: snapshots.length,
        apiBaseUrl,
        exampleUrlWithSession,
        tableRows,
        fullExampleBodyJson: fullExample,
      });
    }, [
      flowId,
      generateLinkForFlowId,
      nodes,
      nodesDependency,
      hideDocumentation,
      flowSummary,
      formData.summary,
      formData.generateSessionId,
      activeFlow?.name,
    ]);

  const apiSchemaExportMutation = useMutation({
    mutationFn: async (action: 'copy' | 'download') => {
      const markdown = await buildApiSchemaMarkdownDocument();
      if (!markdown) {
        throw new Error('Nothing to export');
      }
      return { markdown, action };
    },
    onSuccess: ({ markdown, action }) => {
      if (action === 'copy') {
        void navigator.clipboard.writeText(markdown);
        setNotification({
          message: 'API Schema copied to clipboard',
          type: 'success',
        });
        return;
      }
      if (!flowId) {
        return;
      }
      const blob = new Blob([markdown], {
        type: 'text/markdown;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      const safeName = sanitizeFileName(activeFlow?.name ?? 'agent');
      anchor.download = `fluxprompt-api-schema-${safeName}-${flowId}.md`;
      anchor.click();
      URL.revokeObjectURL(url);
      setNotification({
        message: 'API Schema downloaded',
        type: 'success',
      });
    },
    onError: () => {
      setNotification({
        message: 'Could not export API Schema',
        type: 'error',
      });
    },
  });

  const isValidJson = useCallback((str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditedFlowJson(originalFlowJson);
    setIsJsonEditable(false);
  }, [originalFlowJson]);

  const handleChangeAgent = useCallback(async () => {
    if (!flowId) {
      setNotification({
        message: 'Agent ID is required',
        type: 'error',
      });
      return;
    }

    try {
      setIsLoadingChangeAgent(true);
      const { nodes, edges } = JSON.parse(editedFlowJson);

      const response = await FluxService.updateFlowByJson(flowId, {
        nodes,
        edges,
      });

      setIsJsonEditable(false);
      setNotification({
        message: 'Agent updated successfully',
        type: 'success',
      });

      if (response) {
        window.location.reload();
      }
    } catch (error: any) {
      setNotification({
        message: `Error updating agent: ${error.message || 'Invalid JSON'}`,
        type: 'error',
      });
    } finally {
      setIsLoadingChangeAgent(false);
    }
  }, [editedFlowJson, flowId, setNotification]);

  const handleEditorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const content = e.target.value;
      setEditedFlowJson(content);
    },
    [],
  );

  const isDarkTheme = user?.theme === 'dark';
  const borderDefault = isDarkTheme ? '#475467' : '#EAECF0';
  const borderInput = isDarkTheme ? '#475467' : '#667085';
  const borderSoft = isDarkTheme ? '#475467' : '#D0D5DD';
  const textPrimary = isDarkTheme ? '#F9FAFB' : '#101828';
  const textSecondary = isDarkTheme ? '#D0D5DD' : '#475467';
  const textMuted = '#98A2B3';
  const sectionTitleSx = {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: '0.15px',
    color: isDarkTheme ? '#F9FAFB' : '#000000',
  };
  const switchLabelSx = {
    ml: 0,
    mr: 0,
    gap: 1,
    '& .MuiFormControlLabel-label': {
      fontSize: 16,
      lineHeight: 1.5,
      letterSpacing: '0.15px',
      color: textPrimary,
    },
  };

  return (
    <KitModal
      fullWidth
      dialogContentDividers={false}
      sx={{
        '& .MuiPaper-root': {
          position: 'relative',
          overflow: 'hidden',
          maxWidth: '580px',
          borderRadius: '4px',
          border: `1px solid ${borderDefault}`,
          boxShadow:
            '0px 3px 1px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 2px 0px rgba(0,0,0,0.2)',
        },
        '& .MuiDialogContent-root': {
          padding: '24px',
          borderTop: 'none',
        },
        '& .MuiDialogActions-root': {
          padding: '16px',
        },
      }}
      showCloseButton={false}
      onHide={onHide}
      show={show}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            pb: 1,
            borderBottom: `1px solid ${borderDefault}`,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1.5,
              letterSpacing: '0.15px',
              color: textPrimary,
            }}
          >
            Global Node Settings
          </Typography>
          <IconButton
            aria-label='Close'
            size='small'
            onClick={onHide}
            sx={{ color: textSecondary }}
          >
            <X size={24} />
          </IconButton>
        </Box>

        <Stack
          spacing={2}
          sx={{
            position: 'relative',
            overflow: 'auto',
            height: 'calc(100vh - 200px)',
            paddingRight: '16px',
          }}
        >
          <Stack
            spacing={2}
            sx={{
              pb: 2,
              borderBottom: `1px solid ${borderDefault}`,
            }}
          >
            <Typography
              component='p'
              sx={{
                fontSize: 16,
                lineHeight: 1.75,
                letterSpacing: '0.15px',
                m: 0,
              }}
            >
              <Box component='span' sx={{ color: textPrimary }}>
                Agent Name:{' '}
              </Box>
              <Box component='span' sx={{ color: textMuted }}>
                {activeFlow?.name}
              </Box>
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                width: '100%',
              }}
            >
              <Typography
                component='p'
                sx={{
                  flex: 1,
                  fontSize: 16,
                  lineHeight: 1.75,
                  letterSpacing: '0.15px',
                  m: 0,
                  minWidth: 0,
                }}
              >
                <Box component='span' sx={{ color: textPrimary }}>
                  Agent ID:{' '}
                </Box>
                <Box
                  component='span'
                  sx={{ color: textMuted, wordBreak: 'break-all' }}
                >
                  {activeFlow?.id}
                </Box>
              </Typography>
              <IconButtonTooltip title='Copy Agent Id' onClick={copyFlow}>
                <Copy size={20} color={textSecondary} />
              </IconButtonTooltip>
            </Box>

            <Stack spacing={1}>
              <Typography
                sx={{
                  fontSize: 16,
                  lineHeight: 1.75,
                  letterSpacing: '0.15px',
                  color: textPrimary,
                }}
              >
                Session ID
              </Typography>
              <FormControl fullWidth variant='outlined' size='medium'>
                <Select
                  id='global-settings-session'
                  name='selectedChat'
                  value={activeSession.id}
                  onChange={e => {
                    setActiveSession(e.target.value);
                  }}
                  displayEmpty
                  sx={{
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: borderInput,
                    },
                    '& .MuiSelect-select': {
                      py: 2,
                      px: 1.5,
                      fontSize: 16,
                      color: textMuted,
                    },
                    '& .MuiSvgIcon-root': {
                      color: textSecondary,
                    },
                    bgcolor: isDarkTheme ? '#101828' : '#FFFFFF',
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .Mui-selected': {
                          backgroundColor: isDarkTheme ? '#667085' : '#D0D5DD',
                        },
                        backgroundColor: isDarkTheme ? '#1D2939' : '#F9FAFB',
                      },
                    },
                  }}
                >
                  {sessions.map(session => (
                    <MenuItem key={session.id} value={session.id}>
                      {session.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {formData.generateSessionId && flowId && (
                <Alert
                  severity='info'
                  sx={{
                    boxShadow: 'none',
                    border: 'none',
                    background: isDarkTheme
                      ? 'rgba(255, 255, 255, 0.05)'
                      : '#F9FAFB',
                    color: isDarkTheme ? '#D0D5DD' : '#101828',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: 1.5,
                      color: isDarkTheme ? '#D0D5DD' : '#101828',
                    }}
                  >
                    Visitors can pin a chat session by appending{' '}
                    <Box
                      component='span'
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        backgroundColor: isDarkTheme ? '#1D2939' : '#F9FAFB',
                        padding: '2px 6px',
                        borderRadius: '6px',
                      }}
                    >
                      &session=your-session-id
                    </Box>{' '}
                    to the public chat URL. Example:{' '}
                    <Box
                      component='span'
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        wordBreak: 'break-all',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                        display: 'inline-block',
                        verticalAlign: 'middle',
                        lineHeight: '1.5',
                        backgroundColor: isDarkTheme ? '#1D2939' : '#F9FAFB',
                        padding: '2px 6px',
                        borderRadius: '6px',
                      }}
                    >
                      {`${window.location.origin}/chatbot/${flowId}?session=my-session`}
                    </Box>
                  </Typography>
                </Alert>
              )}
            </Stack>
          </Stack>

          <Stack
            spacing={2}
            sx={{
              pb: 2,
              borderBottom: `1px solid ${borderDefault}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography sx={sectionTitleSx}>Agent Summary</Typography>
              <IconButtonTooltip
                title='Copy Agent Summary'
                onClick={() => {
                  navigator.clipboard.writeText(flowSummary);
                  setNotification({
                    message: 'Agent summary copied to clipboard',
                    type: 'success',
                  });
                }}
              >
                <Copy size={20} color={textSecondary} />
              </IconButtonTooltip>
              {flowSummary && isValidJson(flowSummary) && (
                <Button
                  variant='text'
                  color='primary'
                  onClick={() => setSummaryJsonExpanded(!summaryJsonExpanded)}
                  size='small'
                  sx={{ ml: 'auto', minWidth: 'auto' }}
                >
                  {summaryJsonExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
              )}
            </Box>

            {isLoadingSummary ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : summaryError ? (
              <Typography color='error' variant='body2' sx={{ mb: 2 }}>
                {summaryError}
              </Typography>
            ) : (
              <>
                {(() => {
                  try {
                    const jsonData = JSON.parse(flowSummary);
                    return (
                      <Box
                        sx={{
                          mb: '8px',
                          border: `1px solid ${borderSoft}`,
                          borderRadius: '4px',
                          p: 2,
                          maxHeight: '300px',
                          overflow: 'auto',
                        }}
                      >
                        <JsonView
                          src={jsonData}
                          dark={isDarkTheme}
                          collapsed={summaryJsonExpanded ? false : true}
                          onEdit={edit => {
                            try {
                              const updatedSrc = { ...jsonData };

                              let current = updatedSrc;
                              const path = [...edit.parentPath];

                              if (path.length > 0) {
                                for (let i = 0; i < path.length; i++) {
                                  current = current[path[i]];
                                }
                              }

                              current[edit.indexOrName] = edit.newValue;

                              const updatedJson = JSON.stringify(
                                updatedSrc,
                                null,
                                2,
                              );
                              setFlowSummary(updatedJson);
                              setUserEdited(updatedJson !== originalSummary);

                              onChange({
                                target: {
                                  name: 'summary',
                                  value: updatedJson,
                                },
                              });
                            } catch (e) {
                              console.error('Error updating JSON:', e);
                            }
                          }}
                          editable={true}
                        />
                      </Box>
                    );
                  } catch (e) {
                    return (
                      <TextField
                        id='flowSummary'
                        name='flowSummary'
                        value={flowSummary}
                        fullWidth
                        multiline
                        rows={4}
                        variant='outlined'
                        onChange={e => {
                          setFlowSummary(e.target.value);
                          setUserEdited(e.target.value !== originalSummary);
                          onChange({
                            target: {
                              name: 'summary',
                              value: e.target.value,
                            },
                          });
                        }}
                        InputProps={{
                          readOnly: false,
                          style: {
                            color: isDarkTheme ? '#D0D5DD' : '#101828',
                          },
                        }}
                        placeholder='Enter or generate a summary for this agent...'
                        sx={{
                          mb: '8px',
                          bgcolor: isDarkTheme ? '#1D2939' : '#FFFFFF',
                          borderRadius: '4px',
                          '& .MuiOutlinedInput-root': {
                            fontSize: 12,
                            lineHeight: 1.66,
                            letterSpacing: '0.4px',
                            color: textSecondary,
                            '& fieldset': {
                              borderColor: borderSoft,
                            },
                          },
                        }}
                      />
                    );
                  }
                })()}
                {flowSummary && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 0.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button
                      variant='text'
                      color='primary'
                      onClick={() => setShowJson(!showJson)}
                      size='small'
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.57,
                        letterSpacing: '0.1px',
                        textTransform: 'none',
                        borderRadius: '2px',
                        px: '10px',
                        py: '4px',
                      }}
                    >
                      {showJson ? 'Hide Agent JSON' : 'Show Agent JSON'}
                    </Button>
                    <Button
                      variant='outlined'
                      color='primary'
                      onClick={generateFlowSummary}
                      size='small'
                      disabled={isLoadingSummary}
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.57,
                        letterSpacing: '0.1px',
                        textTransform: 'none',
                        borderRadius: '2px',
                        px: '10px',
                        py: '4px',
                        borderColor: '#3650DE',
                      }}
                    >
                      {isLoadingSummary ? 'Generating...' : 'Regenerate'}
                    </Button>
                  </Box>
                )}
                {!flowSummary && !isLoadingSummary && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 0.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button
                      variant='text'
                      color='primary'
                      onClick={() => setShowJson(!showJson)}
                      size='small'
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.57,
                        letterSpacing: '0.1px',
                        textTransform: 'none',
                        borderRadius: '2px',
                        px: '10px',
                        py: '4px',
                      }}
                    >
                      {showJson ? 'Hide Agent JSON' : 'Show Agent JSON'}
                    </Button>
                    <Button
                      variant='outlined'
                      color='primary'
                      onClick={generateFlowSummary}
                      size='small'
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: 1.57,
                        letterSpacing: '0.1px',
                        textTransform: 'none',
                        borderRadius: '2px',
                        px: '10px',
                        py: '4px',
                        borderColor: '#3650DE',
                      }}
                    >
                      Generate
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Stack>

          {showJson && (
            <Box sx={{ mt: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: '8px',
                }}
              >
                <FormLabel
                  sx={{
                    color: '#475467',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  htmlFor='flowJson'
                >
                  Agent JSON
                  <IconButtonTooltip
                    title='Copy Agent JSON'
                    onClick={copyFlowJson}
                  >
                    <Copy size={14} color='#475467' />
                  </IconButtonTooltip>
                </FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!isJsonEditable ? (
                    <Button
                      variant='text'
                      color='primary'
                      onClick={() => {
                        setOriginalFlowJson(flowJson);
                        setEditedFlowJson(flowJson);
                        setIsJsonEditable(true);
                      }}
                      size='small'
                    >
                      Edit Agent JSON
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant='text'
                        color='secondary'
                        onClick={handleCancelEdit}
                        size='small'
                        disabled={isLoadingChangeAgent}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant='text'
                        color='primary'
                        onClick={handleChangeAgent}
                        size='small'
                        disabled={isLoadingChangeAgent}
                      >
                        {isLoadingChangeAgent ? 'Updating...' : 'Change Agent'}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  mb: '8px',
                  border: `1px solid ${isDarkTheme ? '#475467' : '#EAECF0'}`,
                  borderRadius: '4px',
                  p: 2,
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                {(() => {
                  try {
                    const flowJsonData = JSON.parse(flowJson);

                    return (
                      <Box
                        style={{
                          flex: 1,
                          minHeight: 0,
                          height: '300px',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {isJsonEditable ? (
                          <div
                            style={{
                              flex: 1,
                              minHeight: 0,
                              height: '100%',
                            }}
                          >
                            <TextField
                              value={editedFlowJson}
                              onChange={handleEditorChange}
                              multiline
                              rows={10}
                              fullWidth
                            />
                          </div>
                        ) : (
                          <JsonView
                            src={flowJsonData}
                            dark={isDarkTheme}
                            collapsed={false}
                          />
                        )}
                      </Box>
                    );
                  } catch (e) {
                    return (
                      <Typography color='error'>
                        Error parsing JSON: {(e as Error).message}
                      </Typography>
                    );
                  }
                })()}
              </Box>
            </Box>
          )}

          <Stack spacing={2}>
            <Typography sx={sectionTitleSx}>General Settings</Typography>

            <Stack spacing={1} alignItems='flex-start'>
              {window.location.pathname.includes('/dashboard-beta') && (
                <FormControlLabel
                  name='versionControlEnabled'
                  onChange={onChange}
                  checked={formData.versionControlEnabled}
                  control={<Switch color='primary' />}
                  sx={switchLabelSx}
                  label='Enable Version Control'
                />
              )}

              <FormControlLabel
                name='generateSessionId'
                onChange={onChange}
                checked={formData.generateSessionId}
                control={<Switch color='primary' />}
                sx={switchLabelSx}
                label='Allow passing in custom ID for Chat Sessions'
              />

              <FormControlLabel
                name='isAutotrigger'
                onChange={onChange}
                checked={formData.isAutotrigger}
                control={<Switch color='primary' />}
                sx={switchLabelSx}
                label='Enable Auto Trigger'
              />
            </Stack>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              <Button
                variant='text'
                color='primary'
                size='small'
                onClick={() => setShowAgentSessionModal(true)}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: 1.57,
                  letterSpacing: '0.1px',
                  textTransform: 'none',
                  borderRadius: '2px',
                  px: '10px',
                  py: '4px',
                  gap: 0.5,
                }}
                endIcon={<Plus size={16} />}
              >
                Add new
              </Button>
            </Box>

            <FormControl fullWidth variant='outlined' sx={{ width: '100%' }}>
              <FormLabel
                sx={{
                  display: 'block',
                  mb: 0.5,
                  color: textSecondary,
                  fontSize: 14,
                }}
                htmlFor='selectedNode'
              >
                Target input Node (for email and webhook calls)
              </FormLabel>
              <Select
                name='selectedNode'
                id='selectedNode'
                fullWidth
                value={
                  formData?.selectedNode ||
                  nodeOptionsApiInput[0]?.value ||
                  'none'
                }
                onChange={onChange}
                sx={{
                  mt: 0.5,
                  borderRadius: '4px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: borderSoft,
                  },
                }}
              >
                <MenuItem value={'none'} disabled>
                  Choose API Input Node
                </MenuItem>
                {nodeOptionsApiInput?.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option?.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack spacing={2}>
            <Typography sx={sectionTitleSx}>Triggers</Typography>

            <FormControlLabel
              name='showEmailCalling'
              onChange={onChange}
              checked={formData.showEmailCalling}
              control={<Switch color='primary' />}
              sx={switchLabelSx}
              label='Turn ON Email triggering'
            />
            {formData.showEmailCalling && (
              <>
                <Typography
                  sx={{
                    color: textSecondary,
                    fontSize: '16px',
                    mb: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  Agent Email:{' '}
                  <span
                    style={{
                      color: textMuted,
                    }}
                  >
                    {activeFlow?.id}@{import.meta.env.VITE_FLOW_EMAIL}
                  </span>
                  <IconButtonTooltip
                    title='Copy Agent Email'
                    onClick={copyFlowEmail}
                  >
                    <Copy size={14} />
                  </IconButtonTooltip>
                </Typography>

                <Box>
                  <FormControlLabel
                    name='isPublic'
                    onChange={() => {
                      onChangeIsPublicOrWebhook?.({
                        isPublic: !isPublic,
                      });
                    }}
                    checked={isPublic}
                    control={<Switch color='primary' />}
                    sx={switchLabelSx}
                    label='Allow emails from any address (not just yours)'
                  />
                </Box>
              </>
            )}

            <FormControlLabel
              name='isWebhook'
              onChange={() => {
                onChangeIsPublicOrWebhook?.({
                  webhook: !isWebhook,
                });
              }}
              checked={isWebhook}
              control={<Switch color='primary' />}
              sx={switchLabelSx}
              label='Turn ON Webhook'
            />
            {isWebhook && (
              <>
                <Typography
                  sx={{
                    color: '#475467',
                    fontSize: '16px',
                    mb: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  Agent Webhook URL:{' '}
                  <span
                    style={{
                      color: '#98A2B3',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'inline-block',
                      maxWidth: '500px',
                    }}
                  >
                    {import.meta.env.VITE_API_URL}/flux/api-v2-webhook?flowId=
                    {activeFlow?.id}
                  </span>
                  <IconButtonTooltip
                    title='Copy Webhook URL'
                    onClick={copyWebhookUrl}
                  >
                    <Copy size={14} />
                  </IconButtonTooltip>
                </Typography>
                <Typography
                  sx={{
                    mt: '8px',
                    color: '#475467',
                    fontSize: '12px',
                  }}
                  variant={'body1'}
                >
                  Use this URL to call the agent from a Webhook. Any body of
                  information you pass will be passing into the Data Input node
                  selected under General Settings
                </Typography>
              </>
            )}

            <FormControlLabel
              name='showApiCalling'
              onChange={onChange}
              checked={formData.showApiCalling}
              control={<Switch color='primary' />}
              sx={switchLabelSx}
              label='Turn ON API calling'
            />
            {formData.showApiCalling && (
              <>
                <Typography
                  sx={{
                    color: '#475467',
                    fontSize: '16px',
                    mb: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <span>Agent API URL:</span>
                  <span
                    style={{
                      color: '#98A2B3',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'inline-block',
                      maxWidth: '500px',
                    }}
                  >
                    {generateLinkForFlowId}
                  </span>
                  <IconButtonTooltip
                    title='Copy API URL'
                    onClick={() => {
                      navigator.clipboard.writeText(generateLinkForFlowId!);
                    }}
                  >
                    <Copy size={14} />
                  </IconButtonTooltip>
                </Typography>
                <Typography
                  sx={{
                    mt: '8px',
                    mb: '24px',
                    color: '#475467',
                    fontSize: '12px',
                  }}
                  variant={'body1'}
                >
                  You can use the following link to access the API for the
                  agent.
                </Typography>

                <>
                  <Typography
                    sx={{
                      color: '#475467',
                      fontSize: '16px',
                      mb: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    API Auth:
                    <span
                      style={{
                        color: '#98A2B3',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'inline-block',
                        maxWidth: '500px',
                      }}
                    >
                      api-key: {apiKey}
                    </span>
                    <IconButtonTooltip
                      title='Copy Api Key'
                      onClick={() => {
                        navigator.clipboard.writeText(apiKey!);
                      }}
                    >
                      <Copy size={14} />
                    </IconButtonTooltip>
                  </Typography>
                  <Typography
                    sx={{
                      mt: '8px',
                      mb: '24px',
                      color: '#475467',
                      fontSize: '12px',
                    }}
                    variant={'body1'}
                  >
                    Use the following API Key to authenticate the API request.
                  </Typography>
                </>
                <Box>
                  <Typography
                    sx={{
                      color: '#475467',
                      fontSize: '16px',
                      mb: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    JSON Body Call Example
                    <IconButtonTooltip
                      title='Copy JSON Body Example'
                      onClick={() => {
                        try {
                          navigator.clipboard.writeText(configDisplay);
                          setNotification({
                            message: 'JSON Body Example copied to clipboard',
                            type: 'success',
                          });
                        } catch (e) {
                          setNotification({
                            message: 'Invalid JSON',
                            type: 'error',
                          });
                        }
                      }}
                    >
                      <Copy size={14} color='#475467' />
                    </IconButtonTooltip>
                    <IconButtonTooltip
                      title={`${
                        hideDocumentation ? 'Show' : 'Hide'
                      } documentation field`}
                      onClick={() => setHideDocumentation(!hideDocumentation)}
                    >
                      {hideDocumentation ? (
                        <Eye size={14} color='#475467' />
                      ) : (
                        <EyeSlash size={14} color='#475467' />
                      )}
                    </IconButtonTooltip>
                  </Typography>
                  <Box
                    sx={{
                      mb: '8px',
                      border: `1px solid ${
                        isDarkTheme ? '#475467' : '#EAECF0'
                      }`,
                      borderRadius: '4px',
                      p: 2,
                      maxHeight: '150px',
                      overflow: 'auto',
                      bgcolor: isDarkTheme ? '#1D2939' : '#F9FAFB',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      color: isDarkTheme ? '#D0D5DD' : '#101828',
                    }}
                  >
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {configDisplay}
                    </pre>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    mt: '8px',
                    color: '#475467',
                    fontSize: '12px',
                  }}
                  variant={'body1'}
                >
                  Example JSON body to use when calling the agent.
                </Typography>
                <Button
                  variant='text'
                  color='secondary'
                  onClick={() => setShowFilesExample(!showFilesExample)}
                  size='small'
                  sx={{ mb: '8px' }}
                >
                  {showFilesExample
                    ? 'Hide Files Example'
                    : 'Show Files Example'}
                </Button>
                {showFilesExample && (
                  <>
                    <Typography
                      sx={{
                        color: '#475467',
                        fontSize: '16px',
                        mb: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      JSON Body Call Example With Files:
                      <IconButtonTooltip
                        title='Copy JSON Body Example With Files'
                        onClick={() => {
                          try {
                            navigator.clipboard.writeText(
                              JSON.stringify(JSON.parse(configFull), null, 2),
                            );
                            setNotification({
                              message:
                                'JSON Body Example With Files copied to clipboard',
                              type: 'success',
                            });
                          } catch (e) {
                            setNotification({
                              message: 'Invalid JSON',
                              type: 'error',
                            });
                          }
                        }}
                      >
                        <Copy size={14} color='#475467' />
                      </IconButtonTooltip>
                    </Typography>
                    <Box
                      sx={{
                        mb: '8px',
                        border: `1px solid ${
                          isDarkTheme ? '#475467' : '#EAECF0'
                        }`,
                        borderRadius: '4px',
                        p: 2,
                        maxHeight: '150px',
                        overflow: 'auto',
                        bgcolor: isDarkTheme ? '#1D2939' : '#F9FAFB',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        color: isDarkTheme ? '#D0D5DD' : '#101828',
                      }}
                    >
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {(() => {
                          try {
                            return JSON.stringify(
                              JSON.parse(configFull),
                              null,
                              2,
                            );
                          } catch (e) {
                            return 'Invalid JSON';
                          }
                        })()}
                      </pre>
                    </Box>
                  </>
                )}
              </>
            )}
          </Stack>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pt: 1,
              borderTop: `1px solid ${borderDefault}`,
            }}
          >
            <Typography sx={{ ...sectionTitleSx, fontSize: 14 }}>
              Copy API Schema
            </Typography>
            {apiSchemaExportMutation.isPending ? (
              <CircularProgress size={18} sx={{ ml: '4px' }} />
            ) : (
              <>
                <IconButtonTooltip
                  title='Copy API Schema to clipboard'
                  onClick={() => apiSchemaExportMutation.mutate('copy')}
                >
                  <Copy size={14} color={textSecondary} />
                </IconButtonTooltip>
                <IconButtonTooltip
                  title='Download API Schema as .md'
                  onClick={() => apiSchemaExportMutation.mutate('download')}
                >
                  <DownloadSimple size={14} color={textSecondary} />
                </IconButtonTooltip>
              </>
            )}
          </Box>
        </Stack>
      </Stack>

      <SmallMemorySessionModal
        sessionName={sessionName}
        setSessionName={setSessionName}
        show={showAgentSessionModal}
        onCreateNewUserSession={onCreateNewSession}
        onHide={() => setShowAgentSessionModal(false)}
      />
    </KitModal>
  );
};

export default SettingsModal;
